import { useNotes, useTasks } from "@hq/client-react";
import type { Note } from "@hq/contracts";
import { defineView } from "@hq/core";
import { QuickAdd, TaskList } from "@hq/tasks-ui";
import { Button } from "@z/ui";
import { ArrowUpLeft, FileText, ChevronRight, ChevronDown, ListTodo } from "lucide-react";
import { useId, useState } from "react";
import { useWorkspace } from "../workspace/WorkspaceContext";

export function NoteBreadcrumbs({ note }: { note: Note }) {
  const { data: notes } = useNotes();
  const ws = useWorkspace();
  const trail: Note[] = [];
  const seen = new Set([note.id]);
  let id = note.parentNoteId;
  while (id && !seen.has(id)) {
    seen.add(id);
    const parent = notes?.find((n) => n.id === id);
    if (!parent) break;
    trail.unshift(parent);
    id = parent.parentNoteId;
  }
  if (!trail.length) return null;
  return (
    <nav
      aria-label="Note breadcrumb"
      className="mx-auto flex w-full max-w-[800px] flex-wrap items-center gap-1 px-6 pt-6 @md/note:px-12 text-xs text-fg-muted"
    >
      {trail.map((parent) => (
        <span key={parent.id} className="inline-flex min-w-0 items-center gap-1">
          <button
            className="max-w-48 truncate rounded-sm hover:text-accent focus-ring"
            onClick={() => ws.open({ paneType: "note", noteId: parent.id })}
          >
            {parent.title}
          </button>
          <ChevronRight className="size-3" />
        </span>
      ))}
      <span className="truncate">{note.title}</span>
    </nav>
  );
}

export function NoteConnections({ note }: { note: Note }) {
  const tasks = (useTasks().data ?? []).filter((task) => task.sourceNoteId === note.id);
  const [expanded, setExpanded] = useState<boolean | null>(null);
  const showTasks = expanded ?? tasks.length > 0;
  const tasksId = useId();
  const { data: notes } = useNotes();
  const ws = useWorkspace();
  const linked = (notes ?? []).filter(
    (n) => n.id !== note.id && note.markdown.includes(`](/notes/${n.id})`),
  );
  const backlinks = (notes ?? []).filter(
    (n) => n.id !== note.id && n.markdown.includes(`](/notes/${note.id})`),
  );
  const view = defineView({
    id: `note:${note.id}`,
    name: "Tasks in this note",
    filters: { sourceNoteId: note.id },
    sort: { by: "manual", direction: "asc" },
    display: { showCompleted: true },
  });
  return (
    <div className="mx-auto flex w-full max-w-[800px] flex-col gap-6 px-6 pb-16 @md/note:px-12">
      {!!linked.length && (
        <section aria-label="Linked notes">
          <h3 className="mb-2 text-xs font-medium text-fg-muted">Linked notes</h3>
          <div className="grid gap-2 sm:grid-cols-2">
            {linked.map((n) => (
              <button
                key={n.id}
                onClick={() => ws.open({ paneType: "note", noteId: n.id })}
                className="flex min-w-0 items-start gap-3 rounded-lg border border-border bg-surface/50 p-3 text-left transition-colors hover:bg-surface-raised focus-ring"
              >
                <FileText className="mt-0.5 size-4 shrink-0 text-nav-teal" />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">{n.title}</span>
                  <span className="mt-1 line-clamp-2 text-xs text-fg-muted">
                    {n.markdown.replace(/^#+\s*/gm, "").slice(0, 160) || "Empty note"}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </section>
      )}
      {!!backlinks.length && (
        <section aria-label="Referenced in">
          <h3 className="mb-1 text-xs font-medium text-fg-muted">Referenced in</h3>
          {backlinks.map((n) => (
            <Button
              key={n.id}
              variant="ghost"
              size="sm"
              onClick={() => ws.open({ paneType: "note", noteId: n.id })}
            >
              <ArrowUpLeft />
              {n.title}
            </Button>
          ))}
        </section>
      )}
      <section aria-label="Note tasks" className="border-t border-border pt-4">
        <button
          type="button"
          aria-expanded={showTasks}
          aria-controls={tasksId}
          onClick={() => setExpanded(!showTasks)}
          className="mb-3 flex w-full items-center gap-2 rounded-sm py-1 text-left text-sm text-fg-muted hover:text-fg focus-ring"
        >
          <ListTodo className="size-4 text-nav-purple" />
          <span className="font-medium">Tasks</span>
          <span className="text-xs text-fg-faint">
            {tasks.length
              ? `${tasks.filter((task) => task.status === "completed").length} of ${tasks.length} complete`
              : "Add a task from this note"}
          </span>
          <ChevronDown
            className={`ml-auto size-3.5 transition-transform ${showTasks ? "" : "-rotate-90"}`}
          />
        </button>
        <div id={tasksId} hidden={!showTasks}>
          <TaskList
            view={view}
            onOpenTask={ws.openTask}
            quickAdd={<QuickAdd sourceNoteId={note.id} placeholder="Add a task from this note…" />}
            emptyTitle="No tasks yet"
            emptyDescription="Add one here, or select writing and choose Add as task."
          />
        </div>
      </section>
    </div>
  );
}
